/**
 * Shared types for the Offers (proposals) compare/list surface.
 *
 * `OfferRecord` is a normalized projection of either a `Proposal` row (legacy)
 * or a `TermSheet` row (Issue #1083) with the numeric pricing fields hoisted
 * out of `calculatedTerms`. Concrete normalization happens in
 * `21-consumer-portal/src/pages/proposals/ProposalsPage.tsx` so the sub-components
 * here stay presentation-only.
 *
 * TODO(backend-wiring): once the case-mgmt service emits an `OfferDto` with these
 * fields first-class (highlights, cautions, deadline narrative, lender tone),
 * delete `mapTermSheetToOffer` in ProposalsPage and use the DTO directly.
 */

export type OfferStatus = 'pending' | 'accepted' | 'rejected' | 'expired';
export type OfferLenderTone = 'mint' | 'id' | 'amber';

export interface OfferRecord {
  /** Stable ID — proposal.id or termSheet.id (UUID). */
  id: string;
  /** Short display ID rendered as `caption` mono ID under the lender name. */
  shortId: string;
  /** Lender name (e.g. "Westside Capital"). */
  lender: string;
  /** Card accent tone — drives the optional "Astor pick" mint gradient. */
  lenderTone: OfferLenderTone;
  /** Linked loan/case ID for navigation. */
  loanId?: string;
  /** Property / collateral description, if available. */
  property?: string;
  /** Principal loan amount in dollars. */
  amount: number;
  /** Interest rate as a percent (e.g. 6.95). */
  rate: number;
  /** Term in months (legacy `term * years` flow converts at display). */
  termMonths: number;
  /** Debt service coverage ratio (e.g. 1.31). */
  dscr?: number;
  /** Loan-to-value as a percent (e.g. 70). */
  ltvPercent?: number;
  /** Monthly P&I (or principal-only on bridge) in dollars. */
  monthly: number;
  /** Total closing-cost fees in dollars. */
  fees: number;
  /** Origination as a percent (e.g. 1.25). */
  originationPercent?: number;
  /** Origination in dollars (display-only helper). */
  originationDollars?: number;
  /** Lifecycle status. */
  status: OfferStatus;
  /** Decision deadline — formatted string ready to render in a chip. */
  deadline?: string;
  /** Astor's plain-English narrative — render verbatim, do NOT translate. */
  explain?: string;
  /** Up to 3 positive points (e.g. "Best monthly", "No prepay"). */
  highlights?: string[];
  /** Caution points (e.g. "Higher fees", "12-mo lockout"). */
  cautions?: string[];
}

/**
 * Pre-sorts an array so the lowest-monthly active offer comes first — used as
 * the default "Astor pick" winner. Backend will eventually emit a `winnerId`
 * directly on the response, at which point this can be a fallback only.
 */
export function pickWinnerId(offers: OfferRecord[]): string | undefined {
  const active = offers.filter((o) => o.status === 'pending');
  if (active.length === 0) return undefined;
  let winner = active[0];
  if (!winner) return undefined;
  for (const candidate of active) {
    if (candidate.monthly > 0 && (winner.monthly === 0 || candidate.monthly < winner.monthly)) {
      winner = candidate;
    }
  }
  return winner.id;
}

/**
 * Break-even month between two offers: how many months until the cheaper-monthly
 * offer overcomes the upfront-fee delta. Returns null when math is undefined.
 */
export function breakEvenMonths(winner: OfferRecord, challenger: OfferRecord): number | null {
  const monthlyDelta = challenger.monthly - winner.monthly;
  if (monthlyDelta <= 0) return null;
  const feeDelta = winner.fees - challenger.fees;
  if (feeDelta <= 0) return null;
  return Math.round(feeDelta / monthlyDelta);
}

/**
 * Returns the monthly savings vs the most-expensive competing offer
 * (used to drive the "Best monthly savings" KPI).
 */
export function bestMonthlySavings(offers: OfferRecord[]): { winnerLender: string; savings: number } | null {
  const active = offers.filter((o) => o.status === 'pending' && o.monthly > 0);
  if (active.length < 2) return null;
  const sorted = [...active].sort((a, b) => a.monthly - b.monthly);
  const winner = sorted[0];
  const worst = sorted[sorted.length - 1];
  if (!winner || !worst) return null;
  const savings = worst.monthly - winner.monthly;
  if (savings <= 0) return null;
  return { winnerLender: winner.lender, savings };
}
